import * as React from 'react';
import { FaRedoAlt } from 'react-icons/fa';
import { findDOMNode } from 'react-dom';
import { scrollIntoView } from 'app/infrastructure/platform/browser-dom';
import { ElementLoader } from 'ui';
import { DefaultContainerProps } from 'app/infrastructure/redux';
import { ImportFileStatus } from 'app/area/import/types';
import { ImportCase } from 'app/area/import/types-store';
import { ImportContextMenu } from 'app/area/import/import-context-menu';
import { ImportDetailView } from 'app/area/import/import-detail-view';
import { ImportExpandDetailsButton } from 'app/area/import/import-expand-details-button';
import { NoValueDash, ButtonTertiary } from 'ui/common';
import styles from './import-view.scss';
import { ExpanderBackground } from 'ui/common/dialog-expander/expander-background/expander-background';
import { DEFAULT_DATE_FORMAT } from 'app/infrastructure/data/date';

export interface ImportViewItemProps {
  item: ImportCase;
  fetchingItem: boolean;
  canEditItem: boolean;
  handleEdit: (id: number) => void;
  retryImport: (importCaseId: number) => void;
  deleteImport: (importCaseId: number) => void;
}

export interface ImportViewItemState {
  showDetails?: boolean;
}

const DefaultHeader = ({ primaryKey, showPrimaryKey }) => primaryKey && showPrimaryKey ?
  <div className={`${styles.key}`}>#{primaryKey.value}</div> : null;

export type ImportContainerProps = ImportViewItemProps & DefaultContainerProps;

export class ImportViewItem extends React.Component<ImportContainerProps, ImportViewItemState> {
  private ref: HTMLElement;

  public state: Readonly<ImportViewItemState> = {
    showDetails: false
  };

  private handleSelect = () => {
    this.setState({ showDetails: !this.state.showDetails });
    scrollIntoView(findDOMNode(this.ref) as HTMLElement);
  }

  private handleDelete = () => {
    this.props.deleteImport(this.props.item.id);
  }

  private handleClose = () => {
    this.setState({ showDetails: false });
  }

  private addRef = (el: HTMLElement) => this.ref = el;

  private handleRetry = () => {
    this.props.retryImport(this.props.item.id);
  }

  private dateFormatter = (date: string) => {
    const parsedDate = new Date(date);
    const formattedDate = this.props.intl.formatDate(parsedDate, DEFAULT_DATE_FORMAT);
    const formattedTime = this.props.intl.formatTime(parsedDate);
    return formattedDate + ' ' + formattedTime;
  }

  private getStatusClass = (status: ImportFileStatus) => {
    return {
      [ImportFileStatus.pending]: 'pending',
      [ImportFileStatus.completed]: 'completed',
      [ImportFileStatus.failed]: 'failed'
    }[status];
  }

  private getStatusText = (status: ImportFileStatus) => {
    const { translation } = this.props;
    return {
      [ImportFileStatus.pending]: translation.import_status_pending,
      [ImportFileStatus.completed]: translation.import_status_successful,
      [ImportFileStatus.failed]: translation.import_status_failed
    }[status];
  }

  private renderBody = () => {
    const {
      filename,
      comments,
      createdAtUtc,
      modifiedAtUtc,
      importStatus,
      importSet,
      transCount,
      transCountOk,
      transCountError
    } = this.props.item;

    const { fetchingItem, translation } = this.props;

    return <>
      <div className={`col-lg-4 ${styles.tileHeader}`}>
        <label>{translation.import_file_name}</label>
        <h3>{filename}</h3>
        {!fetchingItem && <div className={`${styles.importItemStatus} ${importStatus && styles[this.getStatusClass(importStatus.v)]}`}>
          {importStatus && this.getStatusText(importStatus.v)}
        </div>}
        {!fetchingItem && importStatus && importStatus.v === ImportFileStatus.failed && <div className={styles.retryButton}>
          <ButtonTertiary onClick={this.handleRetry} icon={FaRedoAlt}>
            {translation.import_retry}
          </ButtonTertiary>
        </div>}
      </div>
      {fetchingItem
        ? <div className="col-lg-8 row">
          <ElementLoader />
        </div>
        : <div className={`${styles.tileDataZone} col-lg-8 row`}>
          <ul className="col-12">
            <li className={`${styles.tileData} ${transCountError > 0 ? styles.errorStyle : ''}`}>
              <span>{transCountError} </span>
              {translation.import_no_of_failed_cases}
            </li>
            <li className={styles.tileData}>
              <span>{transCountOk} </span>
              {translation.import_no_of_ok_cases}
            </li>
            <li className={styles.tileData}>
              <span>{transCount} </span>
              {translation.import_no_of_cases}
            </li>
          </ul>

          <div className={`col-sm-6 col-md-4 ${styles.tileData}`}>
            <label>{translation.import_load_date}</label>
            {createdAtUtc ? <p>{this.dateFormatter(createdAtUtc).trim()}</p> : <NoValueDash />}
          </div>
          <div className={`col-sm-6 col-md-4 ${styles.tileData}`}>
            <label>{translation.import_date}</label>
            {modifiedAtUtc ? <p>{this.dateFormatter(modifiedAtUtc).trim()}</p> : <NoValueDash />}
          </div>
          <div className={`col-sm-6 col-md-4 ${styles.tileData}`}>
            <label>{translation.import_set}</label>
            <p>{importSet && importSet.d}</p>
          </div>

          {comments && <div className={`col-sm-6 col-md-4 ${styles.tileData}`}>
            <label>{translation.import_file_description}</label>
            <p>{comments}</p>
          </div>}

          <div className={`col-md-6 col-md-4 ${styles.tileData}`}>
            <label>{translation.import_status}</label>
            <p>{importStatus && importStatus.d}</p>
          </div>
        </div>}
    </>;
  }

  public render() {
    const { id, transCountError, importStatus } = this.props.item;
    const { fetchingItem, translation } = this.props;

    return <>
      {this.state.showDetails ? <div tabIndex={0} onFocus={this.handleClose} /> : ''}
      <div ref={this.addRef} className={`${styles.tileWrapper} ${this.state.showDetails ? styles.activeTile : ''}`}>
        <div className={`${styles.tile} ${this.state.showDetails ? styles.activeTile : ''}`}>
          <div className={`${styles.tileContent}`}>
            <DefaultHeader {...this.props as any} />
            {!fetchingItem && importStatus && importStatus.v !== ImportFileStatus.pending
              && <ImportContextMenu
                id={id}
                importStatus={importStatus}
                canEdit={this.props.canEditItem}
                handleEdit={this.props.handleEdit}
                handleRetry={this.handleRetry}
                handleDelete={this.handleDelete}
                translation={translation}
              />}
            <div className={`${styles.tileValuesWrapper} row`}>
              {this.renderBody()}
            </div>
          </div>

          {!fetchingItem && transCountError > 0 && <ImportExpandDetailsButton
            id={id}
            showDetails={this.state.showDetails}
            handleClose={this.handleClose}
            handleSelect={this.handleSelect}
            translation={translation}
          />}
        </div>

        <div className={styles.importDetailView}>
          <ImportDetailView
            id={id}
            showDetails={this.state.showDetails}
            handleClose={this.handleClose}
            translation={translation} />
        </div>

          {this.state.showDetails && <ExpanderBackground onClick={this.handleClose}/> }
      </div>
    </>;
  }
}
